// =============================================================================
// HOOK USEGLOBALDETAILEDCOSTS - Coûts détaillés agrégés pour tous les chantiers
// Migré vers React Query (useQuery + invalidation)
// =============================================================================

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CHART_STATUS_COLORS, FRAIS_TYPE_COLORS } from '@/components/charts/chartConfig';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queryKeys } from '@/lib/queryKeys';

// =============================================================================
// TYPES
// =============================================================================

export interface DetailedCostItem {
  name: string;
  value: number;
  color: string;
  pct: number;
}

// Types à exclure du graphique (pénalités de retard, coûts retard)
const TYPES_EXCLUS = ['Pénalité de retard', 'Coût retard', 'cout retard', 'retard'];

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

export function useGlobalDetailedCosts(chantierIds: string[]) {
  const { entrepriseId } = useSubscription();


  // Trier les IDs pour une clé de cache stable
  const sortedIds = useMemo(() => [...chantierIds].sort(), [chantierIds.join(',')]);

  // -------------------------------------------------------------------------
  // Query : charger métriques, frais et factures en parallèle
  // -------------------------------------------------------------------------
  const {
    data,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.charts.globalDetailed(entrepriseId!, sortedIds),
    queryFn: async () => {
      if (!sortedIds.length) {
        return { detailedCosts: [] as DetailedCostItem[], totalCouts: 0 };
      }

      // Charger en parallèle : métriques (pour MO), frais détaillés, factures
      const [metricsRes, fraisRes, facturesRes] = await Promise.all([
        // 1. Métriques pour main d'œuvre
        supabase
          .from('chantier_metrics_realtime')
          .select('metrics')
          .in('chantier_id', sortedIds),

        // 2. Frais chantier détaillés par type
        supabase
          .from('frais_chantier')
          .select('type_frais, montant_ht, montant_total')
          .in('chantier_id', sortedIds),

        // 3. Factures fournisseurs
        supabase
          .from('factures_fournisseurs')
          .select('montant_ht, montant_ttc')
          .in('chantier_id', sortedIds),
      ]);

      const items: DetailedCostItem[] = [];

      // 1. Main d'œuvre (somme de tous les chantiers)
      let totalMO = 0;
      (metricsRes.data || []).forEach((row: any) => {
        const metrics = row.metrics;
        if (metrics?.cout_main_oeuvre_reel) {
          totalMO += metrics.cout_main_oeuvre_reel;
        }
      });

      if (totalMO > 0) {
        items.push({
          name: "Main d'œuvre",
          value: totalMO,
          color: CHART_STATUS_COLORS.mainOeuvre,
          pct: 0,
        });
      }

      // 2. Agréger les frais par type
      const fraisParType: Record<string, number> = {};
      (fraisRes.data || []).forEach((f: any) => {
        const montant = f.montant_ht ?? f.montant_total ?? 0;
        const typeFrais = f.type_frais || 'Autre';

        // Exclure les pénalités et coûts de retard
        const isExclu = TYPES_EXCLUS.some(t =>
          typeFrais.toLowerCase().includes(t.toLowerCase())
        );
        if (isExclu || montant <= 0) return;

        fraisParType[typeFrais] = (fraisParType[typeFrais] || 0) + montant;
      });

      // Ajouter chaque type de frais
      Object.entries(fraisParType).forEach(([typeFrais, montant]) => {
        items.push({
          name: typeFrais,
          value: montant,
          color: FRAIS_TYPE_COLORS[typeFrais] || CHART_STATUS_COLORS.autre,
          pct: 0,
        });
      });

      // 3. Factures fournisseurs
      let totalFournisseurs = 0;
      (facturesRes.data || []).forEach((f: any) => {
        totalFournisseurs += f.montant_ht ?? f.montant_ttc ?? 0;
      });

      if (totalFournisseurs > 0) {
        items.push({
          name: 'Fournisseurs',
          value: totalFournisseurs,
          color: CHART_STATUS_COLORS.fournisseurs,
          pct: 0,
        });
      }

      // Calculer le total et les pourcentages
      const totalCouts = items.reduce((sum, item) => sum + item.value, 0);

      if (totalCouts > 0) {
        items.forEach(item => {
          item.pct = (item.value / totalCouts) * 100;
        });
      }

      // Trier par valeur décroissante
      return {
        detailedCosts: items.sort((a, b) => b.value - a.value),
        totalCouts,
      };
    },
    enabled: !!entrepriseId && sortedIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  return {
    detailedCosts: data?.detailedCosts ?? [],
    totalCouts: data?.totalCouts ?? 0,
    loading,
    refetch: async () => { await refetch(); },
  };
}
